<script setup lang="ts">
import type { FormKitFrameworkContext } from '@formkit/core'
import { ProductExtSelect } from '~/shared/inputs/product-ext.input'
import { CategorySelect } from '~/shared/inputs/category.input'

interface ModuleItem {
  productId?: number | null
  categoryId?: number | null
  type?: 'product' | 'category'
  channelId?: number
}

interface ContextProps {
  channelId?: number | string
}

const props = defineProps<{
  context: FormKitFrameworkContext
  channelId?: number
}>()

// Add a fallback for channelId from the parent repeater
const repeaterChannelId = computed(() => {
  // Try to get from parent props first
  const parentProps = props.context.node.parent?.props as any
  if (parentProps?.channelId) {
    return Number(parentProps.channelId)
  }

  // Try to get from grandparent props (repeater)
  const grandparentProps = props.context.node.parent?.parent?.props as any
  if (grandparentProps?.channelId) {
    return Number(grandparentProps.channelId)
  }

  // Try to get from node props
  const nodeProps = props.context.node.props as any
  if (nodeProps?.channelId) {
    return Number(nodeProps.channelId)
  }

  return undefined
})

const type = ref('product')
const context = props.context
const initialProductLoaded = ref(false)

// Add a direct route-based channelId
const route = useRoute()
const moduleGroupId = computed(() => {
  if (route.params.id) {
    return parseInt(route.params.id as string)
  }
  return undefined
})

const effectiveChannelId = computed(() => {
  // First check direct prop - highest priority
  if (props.channelId) {
    return Number(props.channelId)
  }

  // Then check repeater - second priority
  if (repeaterChannelId.value) {
    return Number(repeaterChannelId.value)
  }

  // Then check parent value - third priority
  const parentValue = context.node.parent?.value
  if (
    parentValue &&
    typeof parentValue === 'object' &&
    'channelId' in parentValue &&
    parentValue.channelId
  ) {
    const parentChannelId = (parentValue as any).channelId
    return Number(parentChannelId)
  }

  // Then check context value - fourth priority
  if (
    context.value &&
    typeof context.value === 'object' &&
    'channelId' in context.value &&
    context.value.channelId
  ) {
    const contextChannelId = (context.value as any).channelId
    return Number(contextChannelId)
  }

  // NO FALLBACK TO moduleGroupId - it's not a valid channelId source
  // Instead, use a fixed default
  return 7
})

// Define product select with channelId in where clause
const productSelect = new ProductExtSelect({
  name: 'productId',
  label: 'Product',
  view: 'product',
  validation: 'required',
  resolveOnLoad: true,
  where() {
    const channelId = effectiveChannelId.value
    // Ensure we're returning the channelId directly, not in an object
    return {
      channelId: channelId,
    }
  },
})

// Define category select with channelId in where clause
const categorySelect = new CategorySelect({
  name: 'categoryId',
  label: 'Category',
  validation: 'required',
  watch: ['channel'],
  where() {
    const channelId = effectiveChannelId.value
    // For category, we need to use the equals format
    return {
      useDefaultChannel: true,
      channelId: {
        equals: channelId,
      },
    }
  },
})

// When type changes, clear the other value
watch(type, (newType) => {
  const channelId = effectiveChannelId.value

  if (newType === 'product') {
    context.node.input({
      type: 'product',
      productId: null,
      categoryId: null,
      channelId,
    })
  } else {
    context.node.input({
      type: 'category',
      productId: null,
      categoryId: null,
      channelId,
    })
  }
})

// Watch for external value changes
watch(
  () => context.value,
  (newValue: ModuleItem) => {
    // Update type based on the IDs present
    if (newValue?.categoryId != null) {
      type.value = 'category'
    } else if (newValue?.productId != null) {
      type.value = 'product'
    }
  },
  { deep: true },
)

// Initialize with product type if no value exists
onMounted(async () => {
  // Get value from parent if we're in a repeater
  const parentValue = context.node.parent?.value as ModuleItem

  // Ensure we have a channelId
  const channelId = effectiveChannelId.value

  if (parentValue) {
    // Determine type based on which ID exists in parent value
    if (parentValue.categoryId != null) {
      type.value = 'category'
      context.node.input({
        type: 'category',
        productId: null,
        categoryId: parentValue.categoryId,
        channelId: Number(channelId),
      })
    } else if (parentValue.productId != null) {
      type.value = 'product'

      // Load initial product options
      try {
        // Pass channelId explicitly as a number to ensure it's used correctly
        const options = await productSelect.initialOptions(
          [parentValue.productId],
          {
            channelId: Number(channelId),
          },
        )
        initialProductLoaded.value = true

        // Set the input value after options are loaded
        context.node.input({
          type: 'product',
          productId: parentValue.productId,
          categoryId: null,
          channelId: Number(channelId),
        })
      } catch (error) {
        console.error('Error loading initial product:', error)
      }
    } else {
      context.node.input({
        type: 'product',
        productId: null,
        categoryId: null,
        channelId: Number(channelId),
      })
    }
  } else {
    context.node.input({
      type: 'product',
      productId: null,
      categoryId: null,
      channelId: Number(channelId),
    })
  }
})

const onProductSelect = (value: number) => {
  const channelId = effectiveChannelId.value
  const newValue = {
    type: 'product',
    productId: value,
    categoryId: null,
    channelId: Number(channelId),
  }
  context.node.input(newValue)
}

const onCategorySelect = (value: number) => {
  const channelId = effectiveChannelId.value
  const newValue = {
    type: 'category',
    productId: null,
    categoryId: value,
    channelId: Number(channelId),
  }
  context.node.input(newValue)
}
</script>

<template>
  <div class="formkit-outer">
    <div class="flex gap-2 mb-4">
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium rounded-md"
        :class="[
          type === 'product'
            ? 'bg-primary-600 text-white'
            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
        ]"
        @click="type = 'product'"
      >
        Product
      </button>
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium rounded-md"
        :class="[
          type === 'category'
            ? 'bg-primary-600 text-white'
            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
        ]"
        @click="type = 'category'"
      >
        Category
      </button>
    </div>

    <template v-if="type === 'product'">
      <FormKit
        type="multiselect"
        :multiple="false"
        v-bind="productSelect.input()"
        :options-loaded="initialProductLoaded"
        :value="Number((context.node.parent?.value as ModuleItem)?.productId)"
        @update:model-value="onProductSelect"
      />
    </template>

    <template v-if="type === 'category'">
      <FormKit
        type="multiselect"
        :multiple="false"
        v-bind="categorySelect.input()"
        :value="Number((context.node.parent?.value as ModuleItem)?.categoryId)"
        @update:model-value="onCategorySelect"
      />
    </template>
  </div>
</template>
